import { Shell } from 'repluggable';
import { default as api } from './apis/api';
import {
  CompRef,
  PageRef,
  DocumentServicesObject,
  RouterRef,
  AppData as DmAppData,
  AppDataComponent,
} from '@wix/document-services-types';
import { DocumentServicesAPI, EditorPointer } from '@wix/editorx-core-api';
import {
  EditorPlatformHostIntegrationAPI,
  PluginInstallationParams,
  PluginActionContext,
  WidgetPointer,
  ToolsEntryEventListener,
  InitAppOptions,
  InstallCallbacks,
  InstallOptions,
  SilentInstallAppsOptions,
  IntentInstallOptions,
  SilentInstallAppsConfig,
  SilentInstallAppsCallbacks,
  HostEditActionsDictionary,
  ExecutionMethodsDictionary,
  AppInstallOption,
  PlatformApiMethods,
  AppInstallOrigin,
  IEditorPlatformPackagesBridge,
  IPublicManifestAPI,
  UniversalSlotPointer,
} from '@wix/editor-platform-host-integration-apis';
import {
  AppData,
  ComponentRef,
  PageVariant,
  Rule,
} from '@wix/editor-platform-sdk-types';
import { initV2 } from './apis/bootstrap/initV2';
import {
  createWorkspaceApi,
  IWorkspaceApiAdapter,
} from './apis/utils/workspaceApiAdapter';
import {
  createExperimentsApi,
  IExperimentsApiAdapter,
} from './apis/utils/experimentsApiAdapter';
import { platformEvents } from './platformEvents';
import { reportBi } from './essentials';
import { PlatformContext } from './types/platformApi';
import { installApps } from './apis/services/unifiedComponents/installation/addApps';
import {
  initIntentInstallServices,
  getIntentInstallServices,
  createIntentInstallAsyncHandlers,
  notifyIntentInstallDidntHappen,
} from './apis/services/intentInstallService';
import { initEditActionsServices } from './apis/services/editActionsServices';
import { initPluginsServices } from './apis/services/plugins/init';
import { initElementsPanelService } from './apis/services/elementsPanelService';
import {
  DocumentOverridesAPIKey,
  EditorPointerServiceAPI,
} from '@wix/editor-platform-feature-store';
import {
  getAppRefreshServices,
  initAppRefreshService,
} from './apis/services/SdkEventHandlerService/services/appRefreshService/appRefreshService';
import { ComponentsService } from '@wix/editor-platform-manifest-services';
import {
  mockPublicManifestAPI,
  mockPublicHierarchyAPI,
} from './apis/services/componentsService/componentsServicePact';
import {
  createEditorPlatformHostIntegrationAPIError,
  EditorPlatformHostIntegrationAPIErrorCode,
} from './editorPlatformHostIntegrationAPIErrors';
import { SlotsService } from './apis/services/plugins/slotsService';
import { SlotPointer } from './apis/services/plugins/slotPointer';

// these variables are being initiated in the "init" function. init function is called only once, but createEditorPlatformHostIntegrationAPI
// can be called multiple times, so need to save and share them
let context: PlatformContext;
const editorPlatformPackagesBridge: Partial<IEditorPlatformPackagesBridge> = {};
let componentsService: ComponentsService;
let intentInstallServices: ReturnType<typeof initIntentInstallServices>;
let editActionsServices: ReturnType<typeof initEditActionsServices>;
let pluginsServices: ReturnType<typeof initPluginsServices>;
let elementsPanelService: ReturnType<typeof initElementsPanelService>;
let appRefreshService: ReturnType<typeof initAppRefreshService>;

function createManifestAPI(
  compoService?: ComponentsService,
): IPublicManifestAPI {
  if (!compoService) {
    console.warn(
      createEditorPlatformHostIntegrationAPIError(
        EditorPlatformHostIntegrationAPIErrorCode.editorPlatformHostIntegrationUninitialized,
        'Editor Platform Host Integration API is not initialized. Returning mock manifest API.',
      ).withAPIName('manifest'),
    );
    return mockPublicManifestAPI;
  }

  return compoService.getPublicManifestAPI();
}

function createSlotPointer(
  documentServicesAPI: DocumentServicesObject,
  manifestAPI: IPublicManifestAPI,
  experimentsAPI: IExperimentsApiAdapter,
  slot: ComponentRef | UniversalSlotPointer,
) {
  const universalSlotPointer = 'hostCompRef' in slot && 'slotId' in slot;
  if (universalSlotPointer) {
    return new SlotPointer(
      SlotPointer.parseUniversalSlotPointer(
        documentServicesAPI,
        manifestAPI,
        experimentsAPI,
        slot,
      ),
    );
  }
  return new SlotPointer({ slotRef: slot });
}

// new API is created for each host (can be multiple)
export function createEditorPlatformHostIntegrationAPI(
  shell: Shell,
): EditorPlatformHostIntegrationAPI {
  const documentServicesAPI = shell.getAPI(
    DocumentServicesAPI,
  ) as unknown as DocumentServicesObject;
  const workspaceAPI: IWorkspaceApiAdapter = createWorkspaceApi(shell);
  const experimentsAPI: IExperimentsApiAdapter = createExperimentsApi(shell);

  // needed for cases when intent install and isRunning function can be called before platform initialization
  let intentInstallServiceAsyncHandlers = createIntentInstallAsyncHandlers();

  let initialized = false;

  return {
    init: (
      platformApiMethods: PlatformApiMethods,
      initAppOptions?: InitAppOptions,
      extraNamespaces?: string[],
    ) => {
      const {
        platformApiMethods: platformAPIMethods,
        componentsService: _componentsService,
        intentInstallServices: _intentInstallServices,
        editActionsServices: _editActionsServices,
        pluginsServices: _pluginsServices,
        elementsPanelService: _elementsPanelServices,
        appRefreshService: _appRefreshService,
      } = api.init(
        {
          workspaceAPI,
          documentServicesAPI,
          experimentsAPI,
          editorPlatformPackagesBridge,
        },
        platformApiMethods,
        editorPlatformPackagesBridge.EditorPointerAPI ??
          ({} as EditorPointerServiceAPI),
        initAppOptions,
        extraNamespaces,
      );

      context = {
        platformApiMethods: platformAPIMethods,
        componentsService: _componentsService,
        intentInstallServices: _intentInstallServices,
        experimentsAPI,
        editorPlatformPackagesBridge,
      };
      componentsService = _componentsService;
      intentInstallServices = _intentInstallServices;
      editActionsServices = _editActionsServices;
      pluginsServices = _pluginsServices;
      elementsPanelService = _elementsPanelServices;
      appRefreshService = _appRefreshService;

      // TEMPORARY
      // TODO - EP-6022 remove after we finish integrating the new types with the editors.
      if (!initialized) {
        initialized = true;
        shell.contributeAPI(
          DocumentOverridesAPIKey,
          () => platformAPIMethods.document,
        );
      }
    },
    initV2,
    getWorkerManager: () => api.getPlatformInfraAPI(),
    getPlatformInfraAPI: () => api.getPlatformInfraAPI(),
    setEditorAPI: (namespace: string, platformAPI: PlatformApiMethods) =>
      api.setEditorAPI(documentServicesAPI, namespace, platformAPI),
    applications: {
      setAppPublicAPI: (apiName: string) =>
        api.applications.setAppPublicAPI(documentServicesAPI, apiName),
      createDependenciesDriver: (
        appDefinitionId: string,
        appVersion?: string,
      ) =>
        api.applications.createDependenciesDriver(
          documentServicesAPI,
          appDefinitionId,
          appVersion,
        ),
      unifiedComponents: {
        installApps: (
          appDefinitionIds: string[],
          installOptions: InstallOptions,
          callbacks?: InstallCallbacks,
        ) =>
          installApps(
            context,
            documentServicesAPI,
            appDefinitionIds,
            installOptions,
            callbacks,
          ),
      },
      install: (
        appDefinitionIds: string[],
        installOptions: InstallOptions,
        callbacks?: InstallCallbacks,
      ) =>
        api.applications.install(
          context,
          documentServicesAPI,
          appDefinitionIds,
          installOptions,
          callbacks,
        ),
      fetchNames: (appDefIds: string[], languageCode?: string) =>
        api.applications.fetchNames(appDefIds, languageCode),
      getAppsMarketListing: (params) =>
        api.applications.getAppsMarketListing(params),
      silentInstall: (
        appsToInstallSilently: string[],
        options: SilentInstallAppsOptions,
        serviceConfig: SilentInstallAppsConfig,
        serviceCallbacks: Partial<SilentInstallAppsCallbacks>,
      ) =>
        api.applications.silentInstall(
          context,
          documentServicesAPI,
          experimentsAPI,
          appsToInstallSilently,
          options,
          serviceConfig,
          serviceCallbacks,
        ),
      intentInstall: async (options: IntentInstallOptions) => {
        // in studio intent install call doesn't know anything about platform initialization
        // so we want to make sure that intent install services are initialized before calling it

        intentInstallServiceAsyncHandlers = createIntentInstallAsyncHandlers();

        intentInstallServiceAsyncHandlers.setIsIntentInstallStarting(true);
        const awaitedIntentInstallServices = await getIntentInstallServices();

        const intentInstallResult = awaitedIntentInstallServices.intentInstall(
          context,
          options,
        );

        intentInstallServiceAsyncHandlers.intentInstallLaunched();
        intentInstallServiceAsyncHandlers.setIsIntentInstallStarting(false);

        return intentInstallResult;
      },

      getIsIntentInstallRunningAsync: async () => {
        if (intentInstallServiceAsyncHandlers.getIsIntentInstallStarting()) {
          await intentInstallServiceAsyncHandlers.waitForIntentInstallToStart();
        }

        return intentInstallServices?.isIntentInstallRunning() || false;
      },

      isIntentInstallRunning: () =>
        intentInstallServices?.isIntentInstallRunning() || false,
      isSilentInstallRunning: () =>
        api.applications.isSilentInstallRunning() ||
        intentInstallServices?.isIntentInstallRunningInSilentMode() ||
        false,
      notifyIntentInstallDidntHappen,
      isAppUpdateAvailable: (appDefId: string, version?: string) =>
        api.applications.isAppUpdateAvailable(
          documentServicesAPI,
          appDefId,
          version,
        ),
      addAppToRefresh: async (appsDefinitionIds: string[]) => {
        const awaitedAppRefreshService = await getAppRefreshServices();
        awaitedAppRefreshService.addAppToRefresh(appsDefinitionIds);
      },
      refreshVisitedApps: async () => {
        const awaitedAppRefreshService = await getAppRefreshServices();
        awaitedAppRefreshService.refreshVisitedApps();
      },
    },
    pages: {
      isPageEssential: (pageId: string) =>
        api.pages.isPageEssential(documentServicesAPI, experimentsAPI, pageId),
      isPageDuplicatable: (pageId: string) =>
        api.pages.isPageDuplicatable(
          documentServicesAPI,
          experimentsAPI,
          pageId,
        ),
    },
    components: {
      getComponentApp: (compRef: CompRef) =>
        api.components.getComponentApp(documentServicesAPI, compRef),
      handleEssentialsComponentsBeforeDelete: (compRefs: CompRef[]) =>
        api.components.handleEssentialsComponentsBeforeDelete(
          context,
          documentServicesAPI,
          compRefs,
        ),
      getPlatformizedComponentData: (componentRef: CompRef) =>
        api.components.getPlatformizedComponentData(
          documentServicesAPI,
          componentRef,
        ),
      isWidgetStretchable: (compRef: CompRef, appData?: DmAppData) =>
        api.components.isWidgetStretchable(
          documentServicesAPI,
          compRef,
          appData,
        ),
      isWidgetFullWidth: (compRef: CompRef, appData?: DmAppData) =>
        api.components.isWidgetFullWidth(documentServicesAPI, compRef, appData),
      getComponentIdentifiers: (compRef: CompRef, appData?: DmAppData) =>
        api.components.getComponentIdentifiers(
          documentServicesAPI,
          compRef,
          appData,
        ),
      isComponentEssential: (compRef: CompRef) =>
        api.components.isComponentEssentialByRef(
          documentServicesAPI,
          experimentsAPI,
          compRef,
        ),
    },
    sdk: {
      overrideImplementation: api.sdk.overrideImplementation,
    },
    pageReplace: {
      setReplacerPage: (
        pageRef: PageRef,
        replacerPageRef: PageRef,
        setAsActive: boolean,
      ) =>
        api.pageReplace.setReplacerPage(documentServicesAPI, {
          replacerPageRef,
          pageRef,
          setAsActive,
        }),
      openPageVariantsPanel: (
        pageTitle: string,
        variants: PageVariant[],
        pageManagingAppDefId: string,
        pageRef: PageRef,
      ) =>
        api.pageReplace.openPageVariantsPanel(documentServicesAPI, context, {
          pageTitle,
          variants,
          pageManagingAppDefId,
          pageRef,
        }),
      addVariantToPage: (
        pageRef: PageRef,
        variantPageRef: PageRef,
        rule: Rule,
        variantId: string,
      ) =>
        api.pageReplace.addVariantToPage(documentServicesAPI, {
          pageRef,
          variantPageRef,
          rule,
          variantId,
        }),
      getPageInfo: (pageRef: PageRef) =>
        api.pageReplace.getPageInfo(documentServicesAPI, pageRef),
      getPageVariations: (pageRef: PageRef) =>
        api.pageReplace.getPageVariations(documentServicesAPI, pageRef),
      isReplaceable: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.isReplaceable(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
      getPageAvailableVariants: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.getPageAvailableVariants(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
      getOriginalPageRef: (pageRef: PageRef) =>
        api.pageReplace.getOriginalPageRef(documentServicesAPI, pageRef),
      isReplacer: (pageRef: PageRef) =>
        api.pageReplace.isReplacer(documentServicesAPI, pageRef),
      isVariant: (pageRef: PageRef) =>
        api.pageReplace.isVariant(documentServicesAPI, pageRef),
      isReplacerActive: (pageRef: PageRef) =>
        api.pageReplace.isReplacerActive(documentServicesAPI, pageRef),
      isRouterExistForPage: (pageRef: PageRef) =>
        api.pageReplace.isRouterExistForPage(documentServicesAPI, pageRef),
      createRouter: (pageRef: PageRef) =>
        api.pageReplace.createRouter(documentServicesAPI, pageRef),
      connectPageToRouter: (routerRef: RouterRef, pageRef: PageRef) =>
        api.pageReplace.connectPageToRouter(
          documentServicesAPI,
          routerRef,
          pageRef,
        ),
      createBlankReplacerPage: (pageRef: PageRef) =>
        api.pageReplace.createBlankReplacerPage(
          context,
          documentServicesAPI,
          pageRef,
        ),
      openDeleteReplacerPageModal: (pageRef: PageRef) =>
        api.pageReplace.openDeleteReplacerPageModal(
          context,
          documentServicesAPI,
          pageRef,
        ),
      getPremiumFeatureName: (appDefinitionId: string, pageRef: PageRef) =>
        api.pageReplace.getPremiumFeatureName(
          documentServicesAPI,
          appDefinitionId,
          pageRef,
        ),
    },
    viewState: {
      getViewState: (appData: AppData, componentRef: ComponentRef) => {
        return api.viewState.getViewState(
          documentServicesAPI,
          appData,
          componentRef,
        );
      },
      setViewState: (
        appData: AppData,
        componentRef: ComponentRef,
        viewState: string,
      ) => {
        api.viewState.setViewState(
          documentServicesAPI,
          appData,
          componentRef,
          viewState,
        );
      },
    },
    canvasOverlay: api.canvasOverlay,
    toolsEntry: {
      register: (listener: ToolsEntryEventListener) => {
        api.toolsEntry.toolsEntryStore.register(listener);
      },
      unregister: (listener: ToolsEntryEventListener) => {
        api.toolsEntry.toolsEntryStore.unregister(listener);
      },
    },
    platformEvents,
    slots: {
      /*
       TODO: refactor slots API, the following methods should be moved inside plugins:
      `getPluginInstallationParams`, `autoInstall`, `autoInstallWithNavigation`, `getAllPluginsInstallationParams`
      */
      getPluginInstallationParams: (
        appDefinitionId: string,
        components: AppDataComponent[],
      ) =>
        api.slots.getPluginInstallationParams(
          documentServicesAPI,
          appDefinitionId,
          components,
        ),
      autoInstall: (
        pluginInstallationParams: PluginInstallationParams,
        pluginActionContext: PluginActionContext,
      ) =>
        api.slots.autoInstall.install(
          context,
          documentServicesAPI,
          pluginInstallationParams,
          pluginActionContext,
          experimentsAPI,
        ),
      autoInstallWithNavigation: (
        pluginInstallationParams: PluginInstallationParams,
        pluginActionContext: PluginActionContext,
      ) => {
        return api.slots.autoInstall.installWithNavigation(
          context,
          documentServicesAPI,
          pluginInstallationParams,
          pluginActionContext,
          experimentsAPI,
        );
      },
      getAllPluginsInstallationParams: (
        appDefinitionId: string,
        components: AppDataComponent[],
        appsOptions: AppInstallOption,
      ) =>
        api.slots.getAllPluginsInstallationParams(
          documentServicesAPI,
          appDefinitionId,
          components,
          context.platformApiMethods,
          experimentsAPI,
          appsOptions,
        ),
      getSlotPlaceholderRefByContentRef: (compRef: CompRef) =>
        pluginsServices.slots.getSlotPlaceholderRefByContentRef(compRef),
      getWidgetHostRefBySlotRef: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetHostRefBySlotRef(compRef),
      getWidgetHostRefByWidgetRef: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetHostRefByWidgetRef(compRef),
      getWidgetSlot: (
        slot: CompRef | UniversalSlotPointer,
        options: { includePrivate: boolean },
      ) =>
        pluginsServices.slots.getWidgetSlot(
          createSlotPointer(
            documentServicesAPI,
            createManifestAPI(componentsService),
            experimentsAPI,
            slot,
          ),
          options,
        ),
      getWidgetSlots: (
        widgetCompRef: CompRef,
        options: { includePrivate: boolean },
      ) => pluginsServices.slots.getWidgetSlots(widgetCompRef, options),
      getSlotPosition: (compRef: CompRef) =>
        pluginsServices.slots.getSlotPosition(compRef),
      getCompRefForSlotSelection: (compRef: CompRef) =>
        pluginsServices.slots.getCompRefForSlotSelection(
          new SlotPointer({ slotRef: compRef }),
        ),
      getSlotInBlocksAppEditor: (slotCompRef: CompRef) =>
        pluginsServices.slots.getSlotInBlocksAppEditor(slotCompRef),
      getWidgetSlotsToMoveContent: (compRef: CompRef) =>
        pluginsServices.slots.getWidgetSlotsToMoveContent(
          new SlotPointer({ slotRef: compRef }),
        ),
      getNearestWidgetHostRef: (compRef: CompRef) =>
        pluginsServices.slots.getNearestWidgetHostRef(compRef),
      hasWidgetSlot: (widgetCompRef: CompRef) =>
        pluginsServices.slots.hasWidgetSlot(widgetCompRef),
      isSlotPublic: (slotCompRef: CompRef) =>
        pluginsServices.slots.isSlotPublic(slotCompRef),
      moveToSlot: (
        fromSlotComponentRef: CompRef,
        toSlotComponentRef: CompRef,
        pluginActionContext: PluginActionContext,
      ) =>
        pluginsServices.slots.moveToSlot(
          context,
          new SlotPointer({ slotRef: fromSlotComponentRef }),
          new SlotPointer({ slotRef: toSlotComponentRef }),
          pluginActionContext,
        ),
      highlightSlot: (compRef: CompRef) =>
        pluginsServices.slots.highlightSlot(
          new SlotPointer({ slotRef: compRef }),
        ),
      unhighlightSlot: (compRef: CompRef) =>
        pluginsServices.slots.unhighlightSlot(
          new SlotPointer({ slotRef: compRef }),
        ),
      isSlotContentPotentialMovable: (compRef: CompRef) =>
        pluginsServices.slots.isSlotContentPotentialMovable(
          new SlotPointer({ slotRef: compRef }),
        ),
      plugins: {
        isPluginComponent: (widgetPluginCompRef: CompRef) =>
          pluginsServices.plugins.isPluginComponent(widgetPluginCompRef),
        showOnPage: (compRef: CompRef) =>
          pluginsServices.plugins.showOnPage(
            new SlotPointer({ slotRef: compRef }),
          ),
        install: (
          slot: ComponentRef | { hostCompRef: CompRef; slotId: string },
          widgetPointer: WidgetPointer,
          pluginActionContext: PluginActionContext,
        ) =>
          pluginsServices.plugins.installWidgetPlugin(
            context,
            createSlotPointer(
              documentServicesAPI,
              createManifestAPI(componentsService),
              experimentsAPI,
              slot,
            ),
            widgetPointer,
            pluginActionContext,
          ),
        uninstall: (
          slot: ComponentRef | { hostCompRef: CompRef; slotId: string },
          pluginActionContext: PluginActionContext,
        ) =>
          pluginsServices.plugins.uninstallWidgetPlugin(
            context,
            createSlotPointer(
              documentServicesAPI,
              createManifestAPI(componentsService),
              experimentsAPI,
              slot,
            ),
            pluginActionContext,
          ),
      },
    },

    get manifest() {
      return createManifestAPI(componentsService);
    },
    get hierarchy() {
      if (!componentsService) {
        console.warn(
          createEditorPlatformHostIntegrationAPIError(
            EditorPlatformHostIntegrationAPIErrorCode.editorPlatformHostIntegrationUninitialized,
            'Editor Platform Host Integration API is not initialized. Returning mock hierarchy API.',
          ).withAPIName('hierarchy'),
        );
        return mockPublicHierarchyAPI;
      }
      return componentsService.getPublicHierarchyAPI();
    },
    panels: {
      addElements: {
        openIfExist: (compRef: CompRef) =>
          api.panels.addElements.openIfExist(documentServicesAPI, compRef),
      },
      upgradeSiteConfirmation: {
        open: (appDefinitionId: string, premiumFeatureName: string) =>
          api.panels.upgradeSiteConfirmation.open(
            documentServicesAPI,
            context,
            appDefinitionId,
            premiumFeatureName,
          ),
      },
      addPanel: {
        getGroups: () =>
          api.panels.addPanel.getGroups(documentServicesAPI, context),
      },
    },
    editActions: {
      getActions: (editorPointer: EditorPointer<CompRef>, tags?: string[]) =>
        editActionsServices.generateEditActions(editorPointer, tags),
      contributeHostActions: (
        hostActions: Partial<HostEditActionsDictionary>,
      ) => api.editActions.contributeHostActions(hostActions),
      contributeExecutionMethods: (
        executionMethods: ExecutionMethodsDictionary,
      ) => api.editActions.contributeExecutionMethods(executionMethods),
    },
    reportBi,
    addons: {
      install: (
        appDefinitionId: string,
        options: { origin: AppInstallOrigin },
      ) =>
        api.applications.installWithConsentModal(
          context,
          documentServicesAPI,
          appDefinitionId,
          {
            appsOptions: {
              [appDefinitionId]: {
                origin: options.origin,
                headlessInstallation: true,
              },
            },
          },
        ),
    },
    contributeEditorPlatformPackagesBridge: <
      Key extends keyof IEditorPlatformPackagesBridge,
    >(
      editorPlatformPackagesBridgeInput: Partial<IEditorPlatformPackagesBridge>,
    ) => {
      for (const item of Object.entries(editorPlatformPackagesBridgeInput)) {
        const [apiName, apiImpl] = item as [
          Key,
          IEditorPlatformPackagesBridge[Key],
        ];

        editorPlatformPackagesBridge[apiName] = apiImpl;
      }
    },
  };
}
